import type { NodeModel } from "@/types/models";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { browserStorage } from "@/lib/browser-storage";

const MAX_VISITED_NODES = 50;

export type TreeHistoryDirection = "back" | "forward";

interface VisitedTreeNode {
  id: number;
  name: string;
}

interface TreeNavigationHistory {
  entries: VisitedTreeNode[];
  index: number;
}

interface TreeNavigationState {
  histories: Record<string, TreeNavigationHistory>;
  recordVisit: (scope: string, node: Pick<NodeModel, "id" | "name">) => void;
  setIndex: (scope: string, index: number) => void;
}

const emptyHistory: TreeNavigationHistory = {
  entries: [],
  index: -1,
};

const toVisitedNode = (node: Pick<NodeModel, "id" | "name">): VisitedTreeNode => ({
  id: Number(node.id),
  name: node.name,
});

export const useTreeNavigationStore = create<TreeNavigationState>()(
  persist(
    (set, get) => ({
      histories: {},
      recordVisit: (scope, node) => {
        const history = get().histories[scope] ?? emptyHistory;
        const current = history.entries[history.index];

        if (current?.id === Number(node.id)) {
          return;
        }

        const entries = [...history.entries.slice(0, history.index + 1), toVisitedNode(node)].slice(-MAX_VISITED_NODES);

        set({
          histories: {
            ...get().histories,
            [scope]: {
              entries,
              index: entries.length - 1,
            },
          },
        });
      },
      setIndex: (scope, index) => {
        const history = get().histories[scope];

        if (!history?.entries[index]) {
          return;
        }

        set({
          histories: {
            ...get().histories,
            [scope]: {
              ...history,
              index,
            },
          },
        });
      },
    }),
    {
      name: "tree-navigation-history",
      storage: createJSONStorage(() => browserStorage),
      partialize: (state) => ({ histories: state.histories }),
    },
  ),
);
